import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const CUSTOMER_SEARCH_DEBOUNCE_MS = 200;

const RELATIONSHIP_TYPES = [
  // Family - Parents/Children
  // { value: 'mother', label: 'Mother' },
  // { value: 'father', label: 'Father' },
  // { value: 'son', label: 'Son' },
  // { value: 'daughter', label: 'Daughter' },
  // // Family - Siblings
  // { value: 'brother', label: 'Brother' },
  // { value: 'sister', label: 'Sister' },
  // // Family - Grandparents/Grandchildren
  // { value: 'grandmother', label: 'Grandmother' },
  // { value: 'grandfather', label: 'Grandfather' },
  // { value: 'grandson', label: 'Grandson' },
  // { value: 'granddaughter', label: 'Granddaughter' },
  // // Family - Extended
  // { value: 'uncle', label: 'Uncle' },
  // { value: 'aunt', label: 'Aunt' },
  // { value: 'nephew', label: 'Nephew' },
  // { value: 'niece', label: 'Niece' },
  // // Family - In-Laws
  // { value: 'mother-in-law', label: 'Mother-in-law' },
  // { value: 'father-in-law', label: 'Father-in-law' },
  // { value: 'sister-in-law', label: 'Sister-in-law' },
  // { value: 'brother-in-law', label: 'Brother-in-law' },
  // // Family - In-Laws (children)
  // { value: 'son-in-law', label: 'Son-in-law' },
  // { value: 'daughter-in-law', label: 'Daughter-in-law' },
  // // Family - Step
  // { value: 'step-mother', label: 'Step-mother' },
  // { value: 'step-father', label: 'Step-father' },
  // { value: 'step-son', label: 'Step-son' },
  // { value: 'step-daughter', label: 'Step-daughter' },
  // Partners
  { value: 'husband', label: 'Husband' },
  { value: 'wife', label: 'Wife' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'partner', label: 'Partner' },
  { value: 'other', label: 'Other' },
  // Friends
  // { value: 'friend', label: 'Friend' },
  // { value: 'close_friend', label: 'Close Friend' },
  // { value: 'best_friend', label: 'Best Friend' },
  // { value: 'neighbor', label: 'Neighbor' },
  // { value: 'coworker', label: 'Coworker' },
  // { value: 'roommate', label: 'Roommate' },
  // // Custom
  // { value: 'other', label: 'Other (specify below)' }
];

export default function AddRelationshipModal({ showModal, onClose, onSuccess, currentCustomerId, currentCustomerName, editingRelationship }) {
  // Edit mode: when editingRelationship is provided, lock the customer and pre-fill relationships
  const isEditMode = !!editingRelationship;

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Relationship state
  const [relationshipToThem, setRelationshipToThem] = useState('');
  const [relationshipToMe, setRelationshipToMe] = useState('');
  const [customRelationshipToThem, setCustomRelationshipToThem] = useState('');
  const [customRelationshipToMe, setCustomRelationshipToMe] = useState('');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  // Refs
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset modal state when opened/closed
  useEffect(() => {
    if (showModal) {
      if (isEditMode && editingRelationship) {
        // Edit mode: pre-fill with existing data
        setSelectedCustomer(editingRelationship.related_customer);
        setSearchQuery(`${editingRelationship.related_customer.first_name} ${editingRelationship.related_customer.last_name}`);
        // Set relationship values based on direction
        if (editingRelationship.direction === 'outgoing') {
          // Outgoing: form perspective matches DB row perspective
          setRelationshipValue(editingRelationship.relationship_to_them, setRelationshipToThem, setCustomRelationshipToThem);
          setRelationshipValue(editingRelationship.relationship_to_me, setRelationshipToMe, setCustomRelationshipToMe);
        } else {
          // Incoming: swap the perspective so the form shows current user's view
          // DB row stores from OTHER customer's perspective, so we swap:
          // - relationship_to_me (from DB) = what current customer IS to the other customer → form's "to them"
          // - relationship_to_them (from DB) = what other customer IS to current customer → form's "to me"
          setRelationshipValue(editingRelationship.relationship_to_me, setRelationshipToThem, setCustomRelationshipToThem);
          setRelationshipValue(editingRelationship.relationship_to_them, setRelationshipToMe, setCustomRelationshipToMe);
        }
      } else {
        // Add mode: clear everything
        setSearchQuery('');
        setSearchResults([]);
        setShowDropdown(false);
        setSelectedCustomer(null);
        setRelationshipToThem('');
        setRelationshipToMe('');
        setCustomRelationshipToThem('');
        setCustomRelationshipToMe('');
        setConsentChecked(false);
      }
      setError('');
      setSuccess('');
    }
  }, [showModal]);

  // Helper to set relationship value from stored value
  const setRelationshipValue = (storedValue, setMain, setCustom) => {
    const found = RELATIONSHIP_TYPES.find(t => t.value === storedValue);
    if (found && found.value !== 'other') {
      setMain(found.value);
      setCustom('');
    } else {
      setMain('other');
      setCustom(storedValue || '');
    }
  };

  // Search customers for autocomplete
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.trim() === '') {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const res = await fetch(
        `${API_URL}/customers/search/autocomplete?q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        // Filter out current customer from results
        const filtered = data.customers.filter(c => c.id !== currentCustomerId);
        setSearchResults(filtered);
        setShowDropdown(true);
      }
    } catch (err) {
      console.error('Failed to search customers:', err);
    } finally {
      setIsSearching(false);
    }
  }, [currentCustomerId]);

  // Debounced search handler
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedCustomer(null);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      searchCustomers(query);
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSearchQuery(`${customer.first_name} ${customer.last_name}`);
    setShowDropdown(false);
    setError('');
  };

  const getEffectiveRelationship = (selected, custom) => {
    if (selected === 'other') {
      return custom || '';
    }
    return selected || '';
  };

  // Find the main value for a stored relationship
  const findMainValue = (storedValue) => {
    const found = RELATIONSHIP_TYPES.find(t => t.value === storedValue);
    if (found && found.value !== 'other') {
      return found.value;
    }
    return 'other';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const effectiveToThem = getEffectiveRelationship(relationshipToThem, customRelationshipToThem);
    const effectiveToMe = getEffectiveRelationship(relationshipToMe, customRelationshipToMe);

    if (!isEditMode && !selectedCustomer) {
      setError('Please select a customer');
      return;
    }

    if (!isEditMode && !consentChecked) {
      setError('Please confirm consent before adding this relationship');
      return;
    }

    if (!effectiveToThem || !effectiveToMe) {
      setError('Please select relationship types for both fields');
      return;
    }

    if (relationshipToThem === 'other' && !customRelationshipToThem.trim()) {
      setError('Please specify the custom relationship type');
      return;
    }

    if (relationshipToMe === 'other' && !customRelationshipToMe.trim()) {
      setError('Please specify the custom relationship type');
      return;
    }

    setIsSaving(true);
    try {
      let res;
      if (isEditMode) {
        // Update existing relationship
        res = await fetch(`${API_URL}/customer-relationships/${editingRelationship.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            relationship_to_them: effectiveToThem,
            relationship_to_me: effectiveToMe,
            direction: editingRelationship.direction
          })
        });
      } else {
        // Create new relationship
        res = await fetch(`${API_URL}/customer-relationships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customer_id: currentCustomerId,
            related_customer_id: selectedCustomer.id,
            relationship_to_them: effectiveToThem,
            relationship_to_me: effectiveToMe
          })
        });
      }

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save relationship');
      }

      const data = await res.json();
      setSuccess(isEditMode ? 'Relationship updated successfully' : 'Relationship created successfully');
      setTimeout(() => {
        onSuccess(data.relationship);
        onClose();
      }, 500);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const formatRelationshipLabel = (value) => {
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-[80vw] h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{isEditMode ? 'Edit Relationship' : 'Add Linked Account'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4" autoComplete="off">
          {/* Customer Search */}
          <div className="mb-6">
            {isEditMode ? (
              /* Edit mode: show locked customer */
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Linked Customer
                </label>
                <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                  <div className="font-medium text-gray-700">
                    {selectedCustomer?.first_name} {selectedCustomer?.last_name}
                  </div>
                  {selectedCustomer?.mobile_phone && (
                    <div className="text-sm text-gray-500">{selectedCustomer.mobile_phone}</div>
                  )}
                </div>
              </div>
            ) : (
              /* Add mode: show search */
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Customer to Link
                </label>
                <div className="relative" ref={dropdownRef}>
                  <input
                    type="text"
                    autoComplete="off"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Type to search for a customer..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-5 w-5 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}

                  {/* Search Results Dropdown */}
                  {showDropdown && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map(customer => (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => handleSelectCustomer(customer)}
                          className="w-full text-left px-4 py-3 hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {customer.first_name} {customer.last_name}
                          </div>
                          {customer.mobile_phone && (
                            <div className="text-sm text-gray-500">{customer.mobile_phone}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {showDropdown && searchQuery && searchResults.length === 0 && !isSearching && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-gray-500">
                      No customers found
                    </div>
                  )}
                </div>

                {/* Selected Customer */}
                {selectedCustomer && (
                  <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div className="font-medium text-indigo-900">
                      {selectedCustomer.first_name} {selectedCustomer.last_name}
                    </div>
                    {selectedCustomer.mobile_phone && (
                      <div className="text-sm text-indigo-600">{selectedCustomer.mobile_phone}</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Relationship Selectors */}
          {selectedCustomer && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentCustomerName} is <span className="font-semibold">{formatRelationshipLabel(getEffectiveRelationship(relationshipToThem, customRelationshipToThem)) || '...'}</span> to {selectedCustomer.first_name}
                </label>
                <select
                  value={relationshipToThem}
                  onChange={(e) => {
                    setRelationshipToThem(e.target.value);
                    setCustomRelationshipToThem('');
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select relationship...</option>
                  {RELATIONSHIP_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {relationshipToThem === 'other' && (
                  <input
                    type="text"
                    autoComplete="off"
                    value={customRelationshipToThem}
                    onChange={(e) => setCustomRelationshipToThem(e.target.value)}
                    placeholder="Enter custom relationship..."
                    className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedCustomer.first_name} is <span className="font-semibold">{formatRelationshipLabel(getEffectiveRelationship(relationshipToMe, customRelationshipToMe)) || '...'}</span> to {currentCustomerName}
                </label>
                <select
                  value={relationshipToMe}
                  onChange={(e) => {
                    setRelationshipToMe(e.target.value);
                    setCustomRelationshipToMe('');
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select relationship...</option>
                  {RELATIONSHIP_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {relationshipToMe === 'other' && (
                  <input
                    type="text"
                    autoComplete="off"
                    value={customRelationshipToMe}
                    onChange={(e) => setCustomRelationshipToMe(e.target.value)}
                    placeholder="Enter custom relationship..."
                    className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>
            </>
          )}

          {/* Consent Checkbox (add mode only) */}
          {!isEditMode && (
            <div className="mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConsentChecked(!consentChecked);
                  }}
                  className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  I confirm that both parties have consented to being added to each other's Linked Accounts list.
                </span>
              </label>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {success}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || (!selectedCustomer && !isEditMode) || (!isEditMode && !consentChecked)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : (isEditMode ? 'Update Relationship' : 'Add Relationship')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
